// src/pages/team/TeamDiscussionsTab.tsx
// Team-scoped chat conversations: the "discuss" half of the hub. Lists
// conversations whose parent is this team, creates new ones with the team as
// parent, and opens them in the chat module.
//
// The shared chat store accepts pagination only, so this view scopes the
// returned page to the team on conversation.parent.id.

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useChat, useI18n } from '@react/hooks';
import {
  Button,
  Badge,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  Input,
  ErrorAlert,
} from '@react/components';
import { useScope } from '@/hooks/useScope';
import { makeEntity } from '@/features/calendar/time';
import { formatRelativeTime } from '@/features/team/logic';
import type { Conversation } from '@core/types/chat/Conversation';

function DiscussionIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Create discussion dialog
// ---------------------------------------------------------------------------

function CreateDiscussionDialog({
  teamId,
  onClose,
  onCreated,
}: {
  teamId: string;
  onClose: () => void;
  onCreated: (conversationId: string) => void;
}) {
  const store = useChat();
  const scope = useScope();
  const { t } = useI18n();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<unknown>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const conversation = await store.CreateConversation({
        type: 'CONVERSATION_TYPE_GROUP',
        name: name.trim(),
        description: description.trim(),
        parent: makeEntity(teamId, 'team', scope),
      } as never);
      const id = conversation?.entity?.id;
      if (id) onCreated(id);
      else onClose();
    } catch (err) {
      setSubmitError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label={t('team.discussions.createTitle')}
    >
      <button
        type="button"
        className="absolute inset-0 bg-foreground/50 backdrop-blur-sm cursor-default"
        aria-label={t('common.cancel')}
        onClick={onClose}
      />
      <Card className="relative w-full max-w-md animate-in fade-in zoom-in-95">
        <form onSubmit={handleSubmit}>
          <CardHeader>
            <CardTitle>{t('team.discussions.createTitle')}</CardTitle>
            <CardDescription>{t('team.discussions.createDescription')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {submitError != null && (
              <ErrorAlert error={submitError} dismissible onDismiss={() => setSubmitError(null)} />
            )}
            <Input
              label={t('team.discussions.nameLabel')}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('team.discussions.namePlaceholder')}
              required
            />
            <Input
              label={t('team.discussions.descriptionLabel')}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('team.discussions.descriptionPlaceholder')}
            />
          </CardContent>
          <CardFooter className="flex justify-end gap-3">
            <Button type="button" variant="secondary" onClick={onClose} disabled={isSubmitting}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={isSubmitting || !name.trim()}>
              {isSubmitting ? t('common.loading') : t('team.discussions.createSubmit')}
            </Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main tab
// ---------------------------------------------------------------------------

interface TeamDiscussionsTabProps {
  teamId: string;
  /** Open the create dialog immediately (e.g. quick action from Overview). */
  initialCreateOpen?: boolean;
  /** Called once the initial-create signal has been consumed. */
  onCreateConsumed?: () => void;
}

export const TeamDiscussionsTab = observer(({ teamId, initialCreateOpen, onCreateConsumed }: TeamDiscussionsTabProps) => {
  const store = useChat();
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);

  useEffect(() => {
    store.ListConversations({
      pagination: { cursor: '', limit: 100, direction: 'forward' },
      sort: [],
      view: 0,
    } as never);
  }, [teamId, store]);

  useEffect(() => {
    if (initialCreateOpen) {
      setCreateOpen(true);
      onCreateConsumed?.();
    }
  }, [initialCreateOpen, onCreateConsumed]);

  const conversations: Conversation[] = useMemo(
    () => (store.items as Conversation[]).filter((c) => c.parent?.id === teamId && !c.is_archived),
    [store.items, teamId],
  );

  const handleOpen = (id: string | undefined) => {
    if (id) navigate(`/chats/${id}`);
  };

  const handleCreated = (conversationId: string) => {
    setCreateOpen(false);
    navigate(`/chats/${conversationId}`);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Badge variant="secondary" size="sm">{conversations.length}</Badge>
          <span className="text-sm text-muted-foreground">{t('team.tab.discussions')}</span>
        </div>
        <Button size="sm" onClick={() => setCreateOpen(true)}>
          <DiscussionIcon className="h-4 w-4 mr-1.5" />
          {t('team.discussions.new')}
        </Button>
      </div>

      {store.error != null && (
        <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />
      )}

      {store.isLoading && conversations.length === 0 ? (
        <div className="space-y-2" aria-hidden="true">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3 p-3 animate-pulse rounded-xl border border-border">
              <div className="h-9 w-9 rounded-full bg-muted" />
              <div className="flex-1 space-y-1.5">
                <div className="h-4 w-40 rounded bg-muted" />
                <div className="h-3 w-64 rounded bg-muted" />
              </div>
              <div className="h-3 w-12 rounded bg-muted" />
            </div>
          ))}
        </div>
      ) : conversations.length === 0 ? (
        <div className="py-12 text-center rounded-xl border border-dashed border-border">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-muted">
            <DiscussionIcon className="h-7 w-7 text-muted-foreground" />
          </div>
          <p className="text-sm font-medium text-foreground">{t('team.discussions.empty')}</p>
          <p className="mt-1 text-xs text-muted-foreground">{t('team.discussions.emptyHint')}</p>
          <Button size="sm" className="mt-4" onClick={() => setCreateOpen(true)}>
            {t('team.discussions.new')}
          </Button>
        </div>
      ) : (
        <ul className="rounded-xl border border-border divide-y divide-border/60 overflow-hidden">
          {conversations.map((c) => {
            const lastAt = c.last_message?.created_at;
            return (
              <li key={c.entity?.id}>
                <button
                  type="button"
                  onClick={() => handleOpen(c.entity?.id)}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-muted/30 transition-colors focus:outline-none focus:ring-2 focus:ring-inset focus:ring-ring"
                >
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-primary flex-shrink-0" aria-hidden="true">
                    <DiscussionIcon className="h-4.5 w-4.5" />
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-medium text-foreground truncate">{c.name}</span>
                    <span className="block text-xs text-muted-foreground truncate">
                      {c.last_message?.content || c.description || t('team.discussions.noMessages')}
                    </span>
                  </span>
                  <span className="flex flex-col items-end gap-1 flex-shrink-0">
                    {lastAt && (
                      <time className="text-xs text-muted-foreground" dateTime={lastAt}>
                        {formatRelativeTime(lastAt, locale)}
                      </time>
                    )}
                    {c.member_count > 0 && (
                      <Badge variant="secondary" size="sm">{c.member_count}</Badge>
                    )}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {createOpen && (
        <CreateDiscussionDialog
          teamId={teamId}
          onClose={() => setCreateOpen(false)}
          onCreated={handleCreated}
        />
      )}
    </div>
  );
});

export default TeamDiscussionsTab;
